import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import AnnouncementBanner from '../../components/AnnouncementBanner';
import '../../styles/dashboards/AdminDashboard.css';

const AdminDashboard = ({ data }) => {
  // Get user and logout function from AuthContext
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Handle logout
  const handleLogout = async () => {
    try {
      const result = await logout();
      if (result.success) {
        navigate('/login');
      } else {
        alert('Failed to logout. Please try again.');
      }
    } catch (error) {
      console.error('Error during logout:', error);
      alert('An error occurred during logout.');
    }
  };

  // Fall back to preventing errors if user is not available
  if (!user) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading user data...</p>
      </div>
    );
  }

  const isSuper = user.role === 'super_admin';

  // Use data from props or fallback to empty arrays
  // Check if there was an error fetching users data
  const usersError = data?.users?.error;
  const halaqasError = data?.halaqas?.error;

  // Get data from different possible structures for backward compatibility
  const users = data?.users?.data || data?.users || [];
  const halaqas = data?.halaqas?.data || data?.halaqas || [];

  console.log('Dashboard data:', data);
  console.log('Users data:', users);
  console.log('Halaqas data:', halaqas);

  // Calculate stats
  const totalUsers = users.length;
  const totalClasses = halaqas.length;
  const activeStudents = users.filter(u => u.role === 'student' && u.active !== false).length;

  return (
    <div className="admin-dashboard">
      {/* Dashboard Header */}
      <div style={{display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '15px'}}>
        <div style={{backgroundColor: '#4a90e2', borderRadius: '8px', padding: '6px 10px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
          <h1 style={{fontSize: '16px', margin: 0, color: 'Black', fontWeight: '500'}}>Admin Dashboard</h1>
        </div>

      </div>

      {/* Announcement Banner */}
      <AnnouncementBanner />

      <div className="stats-section">
        <div className="stats-card">
          <h3>Users</h3>
          <div className="stat-value">{totalUsers || 0}</div>
          <div className="stat-label">Registered users</div>
        </div>
        <div className="stats-card">
          <h3>Classes</h3>
          <div className="stat-value">{totalClasses || 0}</div>
          <div className="stat-label">Active classes</div>
        </div>
        <div className="stats-card">
          <h3>Students</h3>
          <div className="stat-value">{activeStudents || 0}</div>
          <div className="stat-label">Active students</div>
        </div>
      </div>

      <div className="dashboard-panels two-panel-layout">
        {/* Brief Manage Users Panel */}
        <div className="dashboard-panel">
          <div className="panel-header">
            <h2>Manage Users</h2>
            <Link to="/users" className="view-all-link">View All</Link>
          </div>
          <div className="panel-content">
            <p>Manage your center's users, students, teachers, and parents.</p>

            {/* Recent Users Overview - First User Only */}
            {usersError ? (
              <div className="error-state">
                <p>{usersError}</p>
              </div>
            ) : Array.isArray(users) && users.length > 0 ? (
              <div className="users-list">
                {users.slice(0, 1).map((user, index) => {
                  // Check if user data is properly formatted
                  const userName = user.name || 'Unknown';
                  const userEmail = user.email || '';
                  const userRole = user.role || 'unknown';
                  const userId = user._id || index;

                  return (
                    <div className="user-item" key={userId}>
                      <div className="user-info">
                        <div className="user-avatar">{userName.charAt(0) || 'U'}</div>
                        <div>
                          <h3>{userName}</h3>
                          <p>{userEmail}</p>
                          <span className="user-role">{userRole}</span>
                        </div>
                      </div>
                      <div className="user-actions">
                        <Link
                          to={`/users/${userId}`}
                          className="view-button"
                        >
                          View
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state mini">
                <p>No users found</p>
              </div>
            )}

            <div className="panel-actions">
              <Link to="/users" className="panel-button">View Users</Link>
              <Link to="/users/new" className="panel-button primary">Add User</Link>
            </div>
          </div>
        </div>

        {/* Expanded Manage Classes Panel */}
        <div className="dashboard-panel">
          <div className="panel-header">
            <h2>Manage Classes</h2>
            <Link to="/classes" className="view-all-link">View All</Link>
          </div>
          <div className="panel-content">
            <p>Create and manage classes, assign teachers, and schedule sessions.</p>

            {/* Classes Overview - Show More Classes */}
            {halaqasError ? (
              <div className="error-state">
                <p>{halaqasError}</p>
              </div>
            ) : Array.isArray(halaqas) && halaqas.length > 0 ? (
              <div className="classes-list">
                {halaqas.slice(0, 4).map((halaqa, index) => {
                  // Check if halaqa data is properly formatted
                  const className = halaqa.name || 'Unnamed Class';
                  const classId = halaqa._id || index;

                  // Handle teacher information safely
                  let teacherInfo = 'No teacher assigned';
                  if (halaqa.teachers && halaqa.teachers.length > 0) {
                    if (typeof halaqa.teachers[0] === 'object' && halaqa.teachers[0].name) {
                      teacherInfo = halaqa.teachers[0].name;
                    } else {
                      teacherInfo = 'Teacher assigned';
                    }
                  }

                  // Get student count from the studentCount property
                  // This property is now added by the backend API
                  const studentCount = halaqa.studentCount !== undefined
                    ? halaqa.studentCount
                    : (halaqa.students && Array.isArray(halaqa.students)
                        ? halaqa.students.length
                        : 0);

                  return (
                    <div className="class-item" key={classId}>
                      <div className="class-info">
                        <h3>{className}</h3>
                        <p>
                          {teacherInfo} • {studentCount} students
                        </p>
                      </div>
                      <div className="class-actions">
                        <Link
                          to={`/classes/${classId}`}
                          className="view-button"
                        >
                          View
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state mini">
                <p>No classes found</p>
              </div>
            )}

            <div className="panel-actions">
              <Link to="/classes" className="panel-button">View Classes</Link>
              <Link to="/classes/new" className="panel-button primary">Create Class</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/users/new" className="action-card">
            <div className="action-icon">👤</div>
            <h3>Add User</h3>
          </Link>

          <Link to="/classes/new" className="action-card">
            <div className="action-icon">🏫</div>
            <h3>Create Class</h3>
          </Link>

          <Link to="/reports" className="action-card">
            <div className="action-icon">📊</div>
            <h3>View Reports</h3>
          </Link>

          <Link to="/admin/attendance" className="action-card">
            <div className="action-icon">📅</div>
            <h3>Attendance</h3>
          </Link>

          <Link to="/admin/registrations" className="action-card">
            <div className="action-icon">📝</div>
            <h3>Registrations</h3>
          </Link>

          <Link to="/payments" className="action-card">
            <div className="action-icon">💰</div>
            <h3>Payments</h3>
          </Link>

        </div>
      </div>
      
      {/* Mobile Logout Button (only visible on mobile) */}
      <button 
        className="mobile-logout-button" 
        onClick={handleLogout}
        aria-label="Logout"
        style={{ 
          position: 'absolute', 
          top: '0', 
          right: '0', 
          width: '60px', 
          height: '60px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          zIndex: 9999
        }}
      >
        {/* This button is invisible and positioned over the user avatar */}
      </button>
    </div>
  );
};

export default AdminDashboard;